/**
 * Core Clerk auth atoms - Clerk session state tracking
 */

import { Atom } from "@effect-atom/atom-react";
import { Effect, Option } from "effect";
import { readClerkSessionCookieToken } from "../clerk-session-cookie.ts";

/**
 * Clerk session type - using any to avoid internal Clerk type dependencies
 */
export type ClerkSession = any;

/**
 * Clerk auth state including loading status
 */
export interface ClerkAuthState {
  loaded: boolean;
  session: ClerkSession | null;
}

/**
 * Writable atom for Clerk auth state
 *
 * Updated by ClerkAuthSync component when Clerk's state changes
 */
export const clerkAuthStateAtom = Atom.make<ClerkAuthState>({ loaded: false, session: null });

/**
 * Derived atom for just the session (null if not loaded OR not signed in)
 */
export const clerkSessionAtom = Atom.make((get) => {
  const state = get(clerkAuthStateAtom);
  return state.loaded ? state.session : null;
});

/**
 * Mutable ref holding the current Clerk session
 *
 * This is kept in sync by ClerkAuthSync component.
 * The Effect layer reads from this ref to get fresh tokens.
 *
 * @since 1.0.0
 */
export const clerkSessionRef = { current: null as ClerkSession | null };

/**
 * Derived: Is user signed in?
 */
export const isSignedInAtom = Atom.make((get) => {
  const session = get(clerkSessionAtom);
  return session !== null;
});

/**
 * Derived: Current Clerk user
 */
export const clerkUserAtom = Atom.make((get) => {
  const session = get(clerkSessionAtom);
  return session?.user ?? null;
});

/**
 * Tri-state authentication gate, the shared signal every data atom uses to
 * decide whether to fire its RPC.
 *
 * - `"authed"`       — proceed: either Clerk has loaded a live session, OR
 *                      Clerk has not booted yet but the `__session` cookie is
 *                      present (its value IS the JWT the API verifies
 *                      networklessly, so the cookie-fallback token getter can
 *                      authenticate the request before clerk-js finishes).
 * - `"signedOut"`    — Clerk has loaded and there is definitively no session:
 *                      a genuinely signed-out user. Gates resolve to their
 *                      empty value (`Option.none()` / `[]` / `null`).
 * - `"undetermined"` — Clerk has not loaded AND no cookie is present: we do not
 *                      yet know. Gates must NOT resolve to a success value here;
 *                      they stay loading so the "Waiting for authentication…"
 *                      branch (a Success(None)) only ever renders for genuinely
 *                      signed-out users.
 *
 * @since 1.0.0
 */
export type AuthGate = "authed" | "signedOut" | "undetermined";

/**
 * Pure resolver for {@link authGateAtom}. Split out so every branch is unit
 * testable without manipulating `document.cookie`.
 *
 * @since 1.0.0
 */
export const resolveAuthGate = (state: ClerkAuthState, cookiePresent: boolean): AuthGate =>
  state.loaded
    ? (state.session !== null ? "authed" : "signedOut")
    : (cookiePresent ? "authed" : "undetermined");

/**
 * Derived atom exposing the tri-state {@link AuthGate}.
 *
 * Recomputes when `clerkAuthStateAtom` flips (i.e. when clerk-js finishes
 * booting), at which point the authoritative session supersedes the optimistic
 * cookie read.
 *
 * @since 1.0.0
 */
export const authGateAtom = Atom.make((get): AuthGate =>
  resolveAuthGate(get(clerkAuthStateAtom), readClerkSessionCookieToken() !== null)
);

/**
 * Optimistic boolean: is the caller likely authenticated right now?
 *
 * `true` exactly when {@link authGateAtom} is `"authed"`. This is the signal
 * the data layer uses to dispatch RPCs in parallel with clerk-js booting,
 * carried by the cookie-fallback token getter in `rpc-clients/shared.ts`.
 *
 * @since 1.0.0
 */
export const authLikelyAtom = Atom.make((get) => get(authGateAtom) === "authed");

/**
 * Gate an RPC effect on the tri-state {@link AuthGate}.
 *
 * - `"authed"`       → run `effect()`.
 * - `"signedOut"`    → resolve to `signedOutValue` (the empty/none default).
 * - `"undetermined"` → `Effect.never`, keeping the owning atom in its loading
 *                      state until Clerk loads and the gate re-resolves.
 *
 * @since 1.0.0
 */
export const gateOnAuth = <A, E, R>(
  gate: AuthGate,
  signedOutValue: A,
  effect: () => Effect.Effect<A, E, R>,
): Effect.Effect<A, E, R> =>
  gate === "authed"
    ? effect()
    : gate === "signedOut"
    ? Effect.succeed(signedOutValue)
    : Effect.never;

/**
 * {@link gateOnAuth} specialised for the common `Option`-wrapped getter: maps a
 * successful fetch to `Option.some`, resolves a signed-out user to
 * `Option.none()`, and stays loading while undetermined.
 *
 * @since 1.0.0
 */
export const gateOnAuthOption = <A, E, R>(
  gate: AuthGate,
  effect: () => Effect.Effect<A, E, R>,
): Effect.Effect<Option.Option<A>, E, R> =>
  gateOnAuth(gate, Option.none<A>(), () => effect().pipe(Effect.map((value) => Option.some(value))));
